import { useState } from "react";

interface WizardData {
  ad: string;
  tip: string;
  aciklama: string;
  baslamaTarihi: string;
  bitisTarihi: string;
  dahiliNot: string;
  hedefKitle: string;
  mesaj: string;
}

interface Step4Props {
  data: WizardData;
  onTaslak: () => void;
  onBaslat: () => void;
  onGeri: () => void;
}

const HEDEF_KITLE_COUNTS: Record<string, number> = {
  "tum-veliler": 243,
  "aktif-ogrenciler": 198,
  "referans-vermeyen": 126,
  "referans-veren": 72,
  "yeni-kayitlar": 18,
  "odeme-yaklaşan": 24,
  "taahhut-bitmek": 11,
  "ozel-liste": 0,
};

const HEDEF_KITLE_LABELS: Record<string, string> = {
  "tum-veliler": "Tüm Veliler",
  "aktif-ogrenciler": "Aktif Öğrenciler",
  "referans-vermeyen": "Referans Vermeyen Veliler",
  "referans-veren": "Referans Veren Veliler",
  "yeni-kayitlar": "Yeni Kayıtlar",
  "odeme-yaklaşan": "Ödeme Günü Yaklaşanlar",
  "taahhut-bitmek": "Taahhüdü Bitmek Üzere Olanlar",
  "ozel-liste": "Özel Liste",
};

export default function Step4Onizle({ data, onTaslak, onBaslat, onGeri }: Step4Props) {
  const [showConfirm, setShowConfirm] = useState(false);
  const count = HEDEF_KITLE_COUNTS[data.hedefKitle] || 0;
  const tahminiSure = count > 0 ? `~${Math.floor(count * 6 / 60)} saat ${(count * 6) % 60} dakika` : "-";

  const preview = data.mesaj
    .replace(/%veli_adi/g, "Ayşe Hanım")
    .replace(/%ogrenci_adi/g, "Zeynep Yılmaz")
    .replace(/%odeme_tarihi/g, "5 Haziran 2026")
    .replace(/%taahhut_bitis_tarihi/g, "15 Ağustos 2026")
    .replace(/%kampanya_adi/g, data.ad)
    .replace(/%son_tarih/g, data.bitisTarihi)
    .replace(/%telefon/g, "0532 111 2233")
    .replace(/%duraklatma_formu/g, "https://form.link/xyz");

  return (
    <>
      <div className="space-y-5">
        <div>
          <h3 className="text-base font-bold text-gray-900 mb-1">Önizle ve Başlat</h3>
          <p className="text-sm text-gray-500">Kampanya bilgilerinizi gözden geçirin ve başlatın.</p>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {[
            { label: "Kampanya Adı", value: data.ad || "-", icon: "ri-megaphone-line" },
            { label: "Kampanya Türü", value: data.tip || "-", icon: "ri-price-tag-3-line" },
            { label: "Hedef Kitle", value: HEDEF_KITLE_LABELS[data.hedefKitle] || "-", icon: "ri-group-line" },
            { label: "Toplam Kişi", value: count > 0 ? `${count} kişi` : "-", icon: "ri-user-line", highlight: true },
            { label: "Gönderim Aralığı", value: "6 dakika", icon: "ri-timer-line" },
            { label: "Tahmini Tamamlanma", value: tahminiSure, icon: "ri-time-line" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 px-4 py-3">
              <div className="w-7 h-7 flex items-center justify-center text-gray-400 flex-shrink-0">
                <i className={`${item.icon} text-sm`}></i>
              </div>
              <span className="text-xs text-gray-500 w-36 flex-shrink-0">{item.label}</span>
              <span className={`text-sm font-semibold ${item.highlight ? "text-[#25D366]" : "text-gray-800"}`}>
                {item.value}
              </span>
            </div>
          ))}
        </div>

        {/* Message preview */}
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-2">Mesaj Önizleme</p>
          <div className="bg-[#E5DDD5] rounded-xl p-4">
            <div className="bg-white rounded-tl-none rounded-tr-2xl rounded-br-2xl rounded-bl-2xl p-3 max-w-xs shadow-sm">
              <p className="text-[13px] text-gray-800 leading-relaxed whitespace-pre-wrap">
                {preview || "Mesaj metni girilmedi."}
              </p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-gray-400">14:32</span>
                <span className="text-[10px] text-[#25D366]">✓✓</span>
              </div>
            </div>
          </div>
        </div>

        {/* Warning box */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="w-5 h-5 flex items-center justify-center text-amber-500 flex-shrink-0 mt-0.5">
            <i className="ri-alert-line text-base"></i>
          </div>
          <p className="text-sm text-amber-700 leading-relaxed">
            <strong>Dikkat:</strong> Kampanya başlatıldığında sistem her{" "}
            <strong>6 dakikada bir</strong> sıradaki kişiye WhatsApp mesajı gönderecek şekilde çalışacaktır.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={onGeri}
            className="px-4 py-2.5 text-sm border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors flex items-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            Geri Dön
          </button>
          <button
            onClick={onTaslak}
            className="px-4 py-2.5 text-sm border border-[#0B1120] text-[#0B1120] rounded-xl hover:bg-[#0B1120]/5 cursor-pointer whitespace-nowrap transition-colors"
          >
            Taslak Olarak Kaydet
          </button>
          <button
            onClick={() => setShowConfirm(true)}
            className="flex-1 sm:flex-none px-6 py-2.5 text-sm bg-[#25D366] text-white rounded-xl hover:bg-[#1da851] cursor-pointer whitespace-nowrap transition-colors font-semibold flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-fill text-sm"></i>
            </div>
            Kampanyayı Başlat
          </button>
        </div>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowConfirm(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="w-12 h-12 mx-auto bg-[#25D366]/10 rounded-2xl flex items-center justify-center mb-4">
              <div className="w-6 h-6 flex items-center justify-center text-[#25D366]">
                <i className="ri-whatsapp-line text-2xl"></i>
              </div>
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-2">Kampanya başlatılsın mı?</h3>
            <p className="text-sm text-gray-500 text-center leading-relaxed mb-6">
              Bu işlemden sonra seçilen <strong>{count} kişi</strong>ye sırayla WhatsApp mesajları gönderilmeye başlanacaktır.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-2.5 text-sm border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                Vazgeç
              </button>
              <button
                onClick={() => { setShowConfirm(false); onBaslat(); }}
                className="flex-1 py-2.5 text-sm bg-[#25D366] text-white rounded-xl hover:bg-[#1da851] cursor-pointer whitespace-nowrap font-semibold transition-colors"
              >
                Evet, Başlat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}